import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';
import UserAvatar from '../components/UserAvatar';
import {
  Video, VideoOff, Mic, MicOff, PhoneOff, Phone, Monitor, MonitorOff,
  Users, Grid3X3, LayoutGrid, Crown, Hand, Circle, ArrowLeft,
  Presentation, X
} from 'lucide-react';
import { toast, Toaster } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Video Popout Window - Standalone video view for multi-monitor support
 * Syncs state with main meeting room via localStorage events
 */
const L10VideoPopout = () => {
  const { meetingId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  // Get initial state from URL params
  const initialMic = searchParams.get('mic') !== 'false';
  const initialCam = searchParams.get('cam') !== 'false';
  
  // Meeting state
  const [meeting, setMeeting] = useState(null);
  const [participants, setParticipants] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Video state
  const [micEnabled, setMicEnabled] = useState(initialMic);
  const [camEnabled, setCamEnabled] = useState(initialCam);
  const [viewMode, setViewMode] = useState('gallery');
  const [participantStates, setParticipantStates] = useState({});
  const [activeSpeakerId, setActiveSpeakerId] = useState(null);
  const [raisedHands, setRaisedHands] = useState([]);
  const [currentPresenterId, setCurrentPresenterId] = useState(null);
  
  // Screen share state
  const [screenShareState, setScreenShareState] = useState({ isSharing: false, sharedByUserId: null });
  
  // Get current user
  const [currentUser, setCurrentUser] = useState(null);
  
  // Fetch meeting data
  useEffect(() => {
    const fetchData = async () => {
      try {
        const token = localStorage.getItem('token');
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        
        // Get user
        const userStr = localStorage.getItem('user');
        if (userStr) {
          setCurrentUser(JSON.parse(userStr));
        }
        
        // Get meeting
        const response = await axios.get(`${API}/eos/meetings/${meetingId}`, { headers });
        setMeeting(response.data);
        setParticipants(response.data.participants || []);
        setCurrentPresenterId(response.data.presenterOverrideUserId || 
          (response.data.participants?.[response.data.currentPresenterIndex || 0]?.user_id));
        setScreenShareState(response.data.screenShareState || { isSharing: false });
        
        // Initialize participant states
        const states = {};
        (response.data.participants || []).forEach(p => {
          states[p.user_id] = {
            micOn: Math.random() > 0.3,
            camOn: Math.random() > 0.4,
            isSpeaking: false
          };
        });
        setParticipantStates(states);
        
        setLoading(false);
      } catch (error) {
        console.error('Error fetching meeting:', error);
        setLoading(false);
      }
    };
    
    fetchData();
    
    // Poll for updates
    const pollInterval = setInterval(fetchData, 5000);
    return () => clearInterval(pollInterval);
  }, [meetingId]);
  
  // Simulate active speaker
  useEffect(() => {
    if (participants.length === 0) return;
    
    const interval = setInterval(() => {
      const randomIdx = Math.floor(Math.random() * participants.length);
      setActiveSpeakerId(participants[randomIdx]?.user_id);
    }, 4000);
    
    return () => clearInterval(interval);
  }, [participants]);
  
  // Sync state changes to main window via localStorage
  const syncToMain = useCallback((key, value) => {
    const event = { meetingId, key, value, timestamp: Date.now() };
    localStorage.setItem('l10_video_sync', JSON.stringify(event));
    // Trigger storage event for same-origin windows
    window.dispatchEvent(new StorageEvent('storage', {
      key: 'l10_video_sync',
      newValue: JSON.stringify(event)
    }));
  }, [meetingId]);
  
  // Listen for sync events from main window
  useEffect(() => {
    const handleStorage = (e) => {
      if (e.key === 'l10_video_sync') {
        try {
          const event = JSON.parse(e.newValue);
          if (event.meetingId !== meetingId) return;
          
          switch (event.key) {
            case 'mic':
              setMicEnabled(event.value);
              break;
            case 'cam':
              setCamEnabled(event.value);
              break;
            case 'leave':
              window.close();
              break;
            case 'screenShare':
              setScreenShareState(event.value);
              break;
            case 'presenter':
              setCurrentPresenterId(event.value);
              break;
            default:
              break;
          }
        } catch (e) {
          // Ignore JSON parse errors from malformed storage events
        }
      }
    };
    
    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, [meetingId]);
  
  // Handlers
  const handleMicToggle = () => {
    setMicEnabled(prev => {
      const newValue = !prev;
      syncToMain('mic', newValue);
      toast.info(newValue ? 'Microphone unmuted' : 'Microphone muted');
      return newValue;
    });
  };
  
  const handleCamToggle = () => {
    setCamEnabled(prev => {
      const newValue = !prev;
      syncToMain('cam', newValue);
      toast.info(newValue ? 'Camera on' : 'Camera off');
      return newValue;
    });
  };
  
  const handleScreenShare = async () => {
    const newSharing = !screenShareState.isSharing;
    const newState = {
      isSharing: newSharing,
      sharedByUserId: newSharing ? currentUser?.id : null
    };
    setScreenShareState(newState);
    syncToMain('screenShare', newState);
    
    // Update backend with dedicated screen-share endpoint
    try {
      await axios.post(`${API}/eos/meetings/${meetingId}/screen-share`, {
        is_sharing: newSharing,
        shared_by_user_id: newSharing ? currentUser?.id : null
      }, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
    } catch (error) {
      console.error('Error updating screen share:', error);
    }
    
    toast.info(newSharing ? 'Screen sharing started' : 'Screen sharing stopped');
  };
  
  const handleRaiseHand = () => {
    setRaisedHands(prev => {
      const userId = currentUser?.id;
      if (prev.includes(userId)) {
        toast.info('Hand lowered');
        return prev.filter(id => id !== userId);
      } else {
        toast.info('Hand raised');
        return [...prev, userId];
      }
    });
  };
  
  const handleLeave = () => {
    syncToMain('leave', true);
    window.close();
  };
  
  const handleBackToMeeting = () => {
    window.opener?.focus();
    window.close();
  };
  
  // Sort participants
  const sortedParticipants = useMemo(() => {
    return [...participants].sort((a, b) => {
      // Screen sharer first
      if (a.user_id === screenShareState.sharedByUserId) return -1;
      if (b.user_id === screenShareState.sharedByUserId) return 1;
      // Then presenter
      if (a.user_id === currentPresenterId) return -1;
      if (b.user_id === currentPresenterId) return 1;
      // Then active speaker
      if (a.user_id === activeSpeakerId) return -1;
      if (b.user_id === activeSpeakerId) return 1;
      // Then raised hands
      if (raisedHands.includes(a.user_id)) return -1;
      if (raisedHands.includes(b.user_id)) return 1;
      return 0;
    });
  }, [participants, activeSpeakerId, raisedHands, currentPresenterId, screenShareState]);
  
  if (loading) {
    return (
      <div className="h-screen bg-slate-950 flex items-center justify-center">
        <div className="text-slate-400">Loading video...</div>
      </div>
    );
  }
  
  // Video tile component
  const VideoTile = ({ participant, isLarge = false }) => {
    const state = participantStates[participant.user_id] || { micOn: true, camOn: true };
    const isSpeaker = participant.user_id === activeSpeakerId;
    const isPresenter = participant.user_id === currentPresenterId;
    const isScreenSharing = participant.user_id === screenShareState.sharedByUserId;
    const hasRaisedHand = raisedHands.includes(participant.user_id);
    const isCurrentUser = participant.user_id === currentUser?.id;
    
    return (
      <div
        className={`relative rounded-lg overflow-hidden transition-all ${
          isLarge ? 'aspect-video' : 'aspect-square'
        } ${
          isScreenSharing ? 'ring-2 ring-blue-500 ring-offset-2 ring-offset-slate-950' :
          isPresenter ? 'ring-2 ring-purple-500 ring-offset-2 ring-offset-slate-950' :
          isSpeaker ? 'ring-2 ring-green-500 ring-offset-2 ring-offset-slate-950' : ''
        } bg-slate-800`}
      >
        {/* Screen share overlay */}
        {isScreenSharing && (
          <div className="absolute inset-0 bg-gradient-to-br from-blue-900/50 to-slate-900 flex items-center justify-center">
            <div className="text-center">
              <Monitor className="h-12 w-12 mx-auto text-blue-400 mb-2" />
              <span className="text-sm text-blue-300">Screen Sharing</span>
            </div>
          </div>
        )}
        
        {/* Avatar */}
        {!isScreenSharing && (
          <div className="absolute inset-0 bg-gradient-to-br from-slate-700 to-slate-800 flex items-center justify-center">
            <UserAvatar user={participant} size={isLarge ? 'xl' : 'lg'} showStatus={false} />
          </div>
        )}
        
        {/* Speaking indicator */}
        {isSpeaker && !isScreenSharing && (
          <div className="absolute inset-0 border-2 border-green-500 rounded-lg pointer-events-none animate-pulse" />
        )}
        
        {/* Top badges */}
        <div className="absolute top-2 left-2 flex items-center gap-1 flex-wrap">
          {participant.role === 'facilitator' && (
            <Badge className="bg-purple-600/80 text-[10px] py-0 px-1">
              <Crown className="h-2.5 w-2.5" />
            </Badge>
          )}
          {isPresenter && (
            <Badge className="bg-orange-500/80 text-[10px] py-0 px-1">
              <Presentation className="h-2.5 w-2.5 mr-0.5" />
              PRESENTER
            </Badge>
          )}
          {isScreenSharing && (
            <Badge className="bg-blue-500/80 text-[10px] py-0 px-1">
              <Monitor className="h-2.5 w-2.5 mr-0.5" />
              SHARING
            </Badge>
          )}
          {hasRaisedHand && (
            <Badge className="bg-yellow-500/80 text-[10px] py-0 px-1">
              <Hand className="h-2.5 w-2.5" />
            </Badge>
          )}
        </div>
        
        {/* Bottom info */}
        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-2">
          <div className="flex items-center justify-between">
            <span className="text-xs text-white font-medium truncate">
              {participant.display_name || participant.email?.split('@')[0]}
              {isCurrentUser && ' (You)'}
            </span>
            <div className="flex items-center gap-1">
              {!state.micOn && <MicOff className="h-3 w-3 text-red-500" />}
              {!state.camOn && <VideoOff className="h-3 w-3 text-red-500" />}
            </div>
          </div>
        </div>
      </div>
    );
  };
  
  return (
    <div className="h-screen bg-slate-950 flex flex-col">
      <Toaster position="top-right" />
      
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-2 border-b border-slate-800">
        <div className="flex items-center gap-3">
          <Badge className="bg-green-600">
            <Circle className="h-2 w-2 mr-1 fill-current animate-pulse" />
            LIVE
          </Badge>
          <span className="text-white font-medium">{meeting?.title || 'L10 Meeting'}</span>
          <span className="text-slate-400 text-sm">• {participants.length} participants</span>
        </div>
        
        <Button variant="ghost" size="sm" onClick={handleBackToMeeting} className="text-slate-300">
          <ArrowLeft className="h-4 w-4 mr-1" />
          Back to Meeting
        </Button>
      </div>
      
      {/* Screen Share Banner */}
      {screenShareState.isSharing && (
        <div className="bg-blue-900/50 px-4 py-2 flex items-center justify-between">
          <div className="flex items-center gap-2 text-blue-300">
            <Monitor className="h-4 w-4" />
            <span className="text-sm">
              {screenShareState.sharedByUserId === currentUser?.id 
                ? 'You are sharing your screen' 
                : `${participants.find(p => p.user_id === screenShareState.sharedByUserId)?.display_name || 'Someone'} is sharing their screen`
              }
            </span>
          </div>
          {screenShareState.sharedByUserId === currentUser?.id && (
            <Button size="sm" variant="destructive" onClick={handleScreenShare}>
              <MonitorOff className="h-4 w-4 mr-1" />
              Stop Sharing
            </Button>
          )}
        </div>
      )}
      
      {/* Video Grid */}
      <div className="flex-1 p-4 overflow-auto">
        <div className={`grid gap-3 h-full ${
          viewMode === 'speaker' ? 'grid-cols-1' :
          participants.length <= 2 ? 'grid-cols-1 md:grid-cols-2' :
          participants.length <= 4 ? 'grid-cols-2' :
          participants.length <= 6 ? 'grid-cols-2 md:grid-cols-3' :
          'grid-cols-3 md:grid-cols-4'
        }`}>
          {viewMode === 'speaker' ? (
            <>
              {sortedParticipants[0] && (
                <VideoTile participant={sortedParticipants[0]} isLarge />
              )}
              <div className="flex gap-2 overflow-x-auto pb-2">
                {sortedParticipants.slice(1).map(p => (
                  <div key={p.user_id} className="w-32 flex-shrink-0">
                    <VideoTile participant={p} />
                  </div>
                ))}
              </div>
            </>
          ) : (
            sortedParticipants.map(p => (
              <VideoTile key={p.user_id} participant={p} />
            ))
          )}
        </div>
      </div>
      
      {/* Control Bar */}
      <div className="flex items-center justify-center gap-3 px-4 py-4 border-t border-slate-800 bg-slate-900">
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant={micEnabled ? 'secondary' : 'destructive'}
                size="icon"
                className="h-12 w-12 rounded-full"
                onClick={handleMicToggle}
              >
                {micEnabled ? <Mic className="h-5 w-5" /> : <MicOff className="h-5 w-5" />}
              </Button>
            </TooltipTrigger>
            <TooltipContent>{micEnabled ? 'Mute' : 'Unmute'}</TooltipContent>
          </Tooltip>
        </TooltipProvider>
        
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant={camEnabled ? 'secondary' : 'destructive'}
                size="icon"
                className="h-12 w-12 rounded-full"
                onClick={handleCamToggle}
              >
                {camEnabled ? <Video className="h-5 w-5" /> : <VideoOff className="h-5 w-5" />}
              </Button>
            </TooltipTrigger>
            <TooltipContent>{camEnabled ? 'Turn off camera' : 'Turn on camera'}</TooltipContent>
          </Tooltip>
        </TooltipProvider>
        
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant={screenShareState.isSharing && screenShareState.sharedByUserId === currentUser?.id ? 'default' : 'ghost'}
                size="icon"
                className={`h-12 w-12 rounded-full ${
                  screenShareState.isSharing && screenShareState.sharedByUserId === currentUser?.id
                    ? 'bg-blue-600 hover:bg-blue-700'
                    : 'text-slate-300 hover:text-white'
                }`}
                onClick={handleScreenShare}
              >
                {screenShareState.isSharing && screenShareState.sharedByUserId === currentUser?.id 
                  ? <MonitorOff className="h-5 w-5" /> 
                  : <Monitor className="h-5 w-5" />}
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              {screenShareState.isSharing && screenShareState.sharedByUserId === currentUser?.id
                ? 'Stop sharing'
                : 'Share screen'}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
        
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant={raisedHands.includes(currentUser?.id) ? 'default' : 'ghost'}
                size="icon"
                className={`h-12 w-12 rounded-full ${
                  raisedHands.includes(currentUser?.id)
                    ? 'bg-yellow-500 hover:bg-yellow-600 text-black'
                    : 'text-slate-300 hover:text-white'
                }`}
                onClick={handleRaiseHand}
              >
                <Hand className="h-5 w-5" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>{raisedHands.includes(currentUser?.id) ? 'Lower hand' : 'Raise hand'}</TooltipContent>
          </Tooltip>
        </TooltipProvider>
        
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-12 w-12 rounded-full text-slate-300 hover:text-white"
                onClick={() => setViewMode(viewMode === 'gallery' ? 'speaker' : 'gallery')}
              >
                {viewMode === 'gallery' ? <LayoutGrid className="h-5 w-5" /> : <Grid3X3 className="h-5 w-5" />}
              </Button>
            </TooltipTrigger>
            <TooltipContent>{viewMode === 'gallery' ? 'Speaker view' : 'Gallery view'}</TooltipContent>
          </Tooltip>
        </TooltipProvider>
        
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="destructive"
                size="icon"
                className="h-12 w-12 rounded-full"
                onClick={handleLeave}
              >
                <PhoneOff className="h-5 w-5" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Leave call</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>
    </div>
  );
};

export default L10VideoPopout;
